import React, { ReactElement, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, FormControl, FormControlLabel, Radio, RadioGroup, Stack, Typography, useTheme } from "@mui/material";
import { SquareRounded } from "@mui/icons-material";
import { PieChart } from '@mui/icons-material';

import SettingsAccordion from "./SettingsAccordion";
import SettingsCheckbox from "./SettingsCheckbox";
import SettingsSingleSelect, { OptionsValueType } from "./SettingsSingleSelect";
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import { SettingsOptionsContext } from "../SettingsOptions";
import { LegendHorizontalAlign, LegendLayout, LegendVerticalAlign } from "components/widgetParts/WidgetLegend";

export interface WidgetLegendSettings {
  legendAlign?: {
    horizontal?: LegendHorizontalAlign,
    vertical?: LegendVerticalAlign
  }
  legendLayout?: LegendLayout;
  legendShow?: boolean;
}

export const WidgetLegendSettingsDefaults: WidgetLegendSettings = {
  legendAlign: {
    horizontal: "left",
    vertical: "bottom"
  },
  legendLayout: "horizontal",
  legendShow: true,
}

type LayoutOption = {
  label: string;
  value: LegendLayout
}
type LegendButton = {
  horizontalAlign?: LegendHorizontalAlign;
  element?: ReactElement;
  verticalAlign?: LegendVerticalAlign;
}

interface SettingsLegendProps {
  settingsState: WidgetLegendSettings;
}

export default function SettingsLegend(props: SettingsLegendProps): React.JSX.Element {
  const settignsOptions = useContext(SettingsOptionsContext);
  const { i18n } = useTranslation();
  const theme = useTheme();

  const [align, setAlign] = useState<{horizontal?: LegendHorizontalAlign, vertical?: LegendVerticalAlign}>(props.settingsState.legendAlign || {horizontal: "left", vertical: "bottom"});

  const layoutOptions: Array<LayoutOption> = [
    {label: "Horizontal", value: "horizontal"},
    {label: "Vertical", value: "vertical"},
  ];

  const legendButtonGrid: Array<LegendButton> = [
    { horizontalAlign: "left", verticalAlign: "top" },
    { horizontalAlign: "center", verticalAlign: "top" },
    { horizontalAlign: "right", verticalAlign: "top" },
    { horizontalAlign: "left", verticalAlign: "middle" },
    { element: <PieChart /> },
    { horizontalAlign: "right", verticalAlign: "middle" },
    { horizontalAlign: "left", verticalAlign: "bottom" },
    { horizontalAlign: "center", verticalAlign: "bottom" },
    { horizontalAlign: "right", verticalAlign: "bottom" },
  ];

  const showLegend = props.settingsState.legendShow ?? WidgetLegendSettingsDefaults.legendShow;

  return (
    <>
      {/* Legend Settings */}
      <SettingsAccordion label={i18n.t("legend")} showBottomDivider={false}>
        <SettingsCheckbox
          checked={showLegend}
          keyid="legendShow"
          label={i18n.t("widget-settings.show-legend")}
        />
        {showLegend &&
          <>
            <SettingsSingleSelect
              defaultValue={layoutOptions.find((item: OptionsValueType) => (
                item.value === props.settingsState.legendLayout
              )) ?? layoutOptions[0]}
              keyid="legendLayout"
              label={i18n.t("widget-settings.legend-direction")}
              options={layoutOptions}
            />
            <Stack direction="row">
              <Typography sx={{ flex: 1 }}>{i18n.t("widget-settings.legend-position")}</Typography>
              <Box sx={{ flex: 2, marginLeft: 3 }}>
                <FormControl>
                  <RadioGroup
                    value={{align: align.horizontal, verticalAlign: align.vertical}}
                    className="radio-group"
                    sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}
                    onChange={(event: React.ChangeEvent, value: string) => {
                      const [vertical, horizontal] = value.split(" ");
                      const newAlign = {
                        horizontal: horizontal as LegendHorizontalAlign,
                        vertical: vertical as LegendVerticalAlign
                      }
                      setAlign(newAlign);
                      settignsOptions?.updateSettingsState("legendAlign", newAlign);
                    }}>
                    {legendButtonGrid.map((item, index) => {
                      if (item.verticalAlign && item.horizontalAlign) {
                        return (
                          <FormControlLabel
                            key={index}
                            className={
                              `radio-label ${(item.verticalAlign === align.vertical &&
                                item.horizontalAlign === align.horizontal) && "checked"}`
                            }
                            sx={{
                              "border": `1px solid ${theme.colors.alpha.black[30]}`,
                              "borderRadius": `${theme.general.borderRadiusSm}`,
                              '&.MuiFormControlLabel-root.checked': {
                                background: "rgb( 34, 170, 255)"
                              },
                              '&.MuiFormControlLabel-root span svg': {
                                visibility: "hidden"
                              }
                            }}
                            label=""
                            value={`${item.verticalAlign} ${item.horizontalAlign}`}
                            control={(
                              <Radio icon={<CheckBoxOutlineBlankRoundedIcon />} checkedIcon={<SquareRounded />} />
                            )}
                          />
                        )
                      } else if (item.element) {
                        return (
                          <FormControlLabel
                            key={index}
                            className="non-radio-icon"
                            disabled
                            sx={{ justifyContent: "center" }}
                            label=""
                            value=""
                            control={(item.element)}
                          />
                        )
                      }
                    })}
                  </RadioGroup>
                </FormControl>
              </Box>
            </Stack>
          </>
        }
      </SettingsAccordion>
    </>
  )
}